// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DragSource } from 'react-dnd';
import { isEmpty, defer } from 'lodash';

// Utils
import { stopPropagationOnly } from '../../utils/domUtil';
import { propIn } from '../../../common/utils/immutableHelper';

// Measurements
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Actions
import { startAttachMode } from '../../utils/dnd/dndActions';

// Components
import EdgeDragHandle from './EdgeDragHandle';

// Constants
import { LINE_EDGE_DND_TYPE } from '../../../common/lines/lineConstants';
import { ATTACH_MODE_TYPES } from '../../utils/dnd/dndConstants';

const mapDispatchToProps = (dispatch) => ({
    dispatchStartAttachMode: (hoveredElementId) =>
        dispatch(startAttachMode(hoveredElementId, ATTACH_MODE_TYPES.LINE_EDGE)),
});

const edgeDragSource = {
    beginDrag({
        edge,
        elementId,
        toggleLineVisibility,
        pos,
        element,
        dispatchPointDragStart,
        dispatchStartAttachMode,
    }) {
        defer(toggleLineVisibility);
        dispatchPointDragStart();

        // Begin in the snapped mode, if currently snapped to a fixed point
        const snappedElementId = propIn(['content', edge, 'elementId'], element);
        const isFixed = propIn(['content', edge, 'fixed'], element);

        if (isFixed && snappedElementId) {
            requestAnimationFrame(() => {
                dispatchStartAttachMode(snappedElementId);
            });
        }

        const initialScrollPoint = measurementsRegistry.getCanvasViewportScrollAsPoint();

        return { id: elementId, element, edge, pos, initialScrollPoint };
    },
    endDrag(props, monitor) {
        const { updateLineEdge, edge, toggleLineVisibility } = props;

        // Doing nothing if it was not dropped onto a compatible target
        const dropResult = monitor.getDropResult();
        if (!monitor.didDrop() || isEmpty(dropResult) || dropResult.ignore) {
            toggleLineVisibility();
            return;
        }

        const { newPosition, newControl } = dropResult;

        if (!newPosition) {
            toggleLineVisibility();
            return;
        }

        updateLineEdge({ edge, newPosition, newControl });
        toggleLineVisibility();
    },
};

const dragCollect = (connector, monitor) => ({
    // Call inside render() to let React handle the drag event
    connectDragSource: connector.dragSource(),
    connectDragPreview: connector.dragPreview(),
    // Get current drag state from the monitor
    isDragging: monitor.isDragging(),
});

@connect(null, mapDispatchToProps)
@DragSource(LINE_EDGE_DND_TYPE, edgeDragSource, dragCollect)
class EdgeDragHandleDragSource extends React.Component {
    render() {
        return <EdgeDragHandle {...this.props} />;
    }
}

/**
 * Needs to be a React component so that the parent's (Line) ref connects correctly.
 */
/* eslint-disable react/no-multi-comp */
class ConnectedEdgeDragHandle extends React.Component {
    render() {
        const { show } = this.props;
        if (!show) return null;

        return <EdgeDragHandleDragSource onMouseDown={stopPropagationOnly} isHitArea {...this.props} />;
    }
}

ConnectedEdgeDragHandle.propTypes = {
    show: PropTypes.bool,
    pos: PropTypes.object,
    edge: PropTypes.string.isRequired,
    elementId: PropTypes.string.isRequired,
    updateLineEdge: PropTypes.func,
    toggleLineVisibility: PropTypes.func,
    dispatchPointDragStart: PropTypes.func,
    dispatchStartAttachMode: PropTypes.func,
    gridSize: PropTypes.number,
    origin: PropTypes.object,
};

export default ConnectedEdgeDragHandle;
